import { useNavigate } from "@solidjs/router";
import { onMount, Show } from "solid-js";
import { AppBar } from "../components/AppBar";
import { Button } from "../components/Button";
import { OperatedBy } from "../components/OperatedBy";
import { km } from "../format";
import { type ItineraryPlan, parkingShortName, planTimes, priceDetail } from "../itinerary";
import { pricingPlans } from "../state/pricing";
import { tripPlan } from "../state/tripPlan";

const minutes = (sec: number) => Math.max(1, Math.ceil(sec / 60));
const meters = (m: number) => `${Math.round(m / 10) * 10} m`;

/**
 * Figma C1b "Itinéraire" (76:11362), bike only: walk to the bike, ride to the parking nearest the
 * destination, walk on; times, distance and the price from the bike's GBFS plan. "Réserver le vélo"
 * follows this itinerary and opens the booking modal on the map (C4).
 */
export function Itinerary() {
  const navigate = useNavigate();
  const plan = tripPlan.chosen;
  // Reloaded here without a pick: back to the search.
  onMount(() => {
    if (!plan()) navigate("/recherche", { replace: true });
  });
  const book = (p: ItineraryPlan) => {
    tripPlan.follow(p);
    navigate(`/map?vehicle=${encodeURIComponent(p.bike.vehicleId as string)}&book=1`);
  };

  return (
    <main class="screen bg-surface">
      <AppBar title="Itinéraire" back="/recherche" />
      <Show when={plan()}>
        {(p) => {
          const times = planTimes(Date.now(), p());
          const gbfs = p().bike.pricingPlanId
            ? pricingPlans()[p().bike.pricingPlanId as string]
            : undefined;
          const price = gbfs ? priceDetail(gbfs, p().ride.sec) : undefined;
          return (
            <div class="screen-body px-5 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <h1 class="text-xl font-semibold leading-[1.25] tracking-[0.2px] text-violet">
                {p().from.name} &gt; {p().to.name}
              </h1>
              <p class="mt-[6px] text-[13px] leading-[1.25] tracking-[0.13px] text-text">
                {times.totalMin} min · départ {times.departure} · arrivée {times.arrival}
              </p>

              {/* Timeline: times on the left, nodes on the line, labels on the right. */}
              <ol class="mt-6">
                <Stop time={times.departure} label={p().from.name} />
                <Walk text={`${minutes(p().walk.sec)} min à pied`} />
                <Stop time={times.bikeAt} label={`Vélo à ${meters(p().walk.meters)}`} />
                <li class="grid grid-cols-[52px_24px_1fr]">
                  <span />
                  <span class="mx-auto w-[3px] bg-indigo" />
                  <div class="my-2 rounded-[10px] bg-surface2 px-3 py-[10px] text-[13px] leading-[1.25] tracking-[0.13px] text-strong">
                    <div class="flex items-center gap-2 font-medium">
                      <img src="/icons/bike-indigo-20b.svg" width="20" height="20" alt="" />
                      Vélo Pony · {minutes(p().ride.sec)} min · {km(p().ride.meters)}
                    </div>
                    <div class="mt-[6px] flex items-center gap-2 text-text">
                      <span class="flex h-5 w-5 items-center justify-center rounded-full border-[1.25px] border-white bg-indigo">
                        <img src="/icons/bike-white-12.svg" width="12.5" height="12.5" alt="" />
                      </span>
                      {p().bikesNearby > 1
                        ? `${p().bikesNearby} vélos disponibles à ${meters(p().walk.meters)}`
                        : `1 vélo disponible à ${meters(p().walk.meters)}`}
                    </div>
                    <Show when={price}>
                      {(pr) => (
                        <div class="mt-[6px] text-xs font-medium tracking-[0.12px] text-violet">
                          {pr().total} {pr().detail}
                        </div>
                      )}
                    </Show>
                  </div>
                </li>
                <Stop
                  time={times.parkAt}
                  label={`Stationnement ${parkingShortName(p().parking.name)}`}
                />
                <Walk text={`${minutes(p().last.sec)} min à pied`} />
                <Stop time={times.arrival} label="Arrivée" last />
              </ol>

              <div class="mt-auto pt-6">
                <OperatedBy />
                <Button class="mt-3" onClick={() => book(p())}>
                  Réserver le vélo
                </Button>
              </div>
            </div>
          );
        }}
      </Show>
    </main>
  );
}

function Stop(props: { time: string; label: string; last?: boolean }) {
  return (
    <li class="grid grid-cols-[52px_24px_1fr] items-center">
      <span class="text-[13px] font-medium leading-[1.25] tracking-[0.13px] text-strong">
        {props.time}
      </span>
      <span
        class={`mx-auto h-[14px] w-[14px] rounded-full border-[3px] border-indigo ${props.last ? "bg-indigo" : "bg-white"}`}
      />
      <span class="text-[15px] font-medium leading-[1.25] tracking-[0.15px] text-strong">
        {props.label}
      </span>
    </li>
  );
}

function Walk(props: { text: string }) {
  return (
    <li class="grid grid-cols-[52px_24px_1fr] items-center">
      <span />
      <span class="mx-auto h-11 w-0 border-l-[3px] border-dotted border-indigo" />
      <span class="flex items-center gap-[6px] text-[13px] leading-[1.25] tracking-[0.13px] text-text">
        <img src="/icons/walk-violet-20.svg" width="20" height="20" alt="" />
        {props.text}
      </span>
    </li>
  );
}
